import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import ConsentPanel from "../../components/delivery/ConsentPanel";
import ExpiredPanel from "../../components/delivery/ExpiredPanel";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import ExpiryCountdown from "../../components/delivery/ExpiryCountdown";
import SelectionNameGate from "../../components/selection/SelectionNameGate";
import SelectionFolderBrowser from "../../components/selection/SelectionFolderBrowser";
import { SELECTION_CONSENT_CLAUSES } from "../../utils/deliveryConsent";
import { useSelectionPayload } from "../../hooks/useSelectionPayload";
import {
    getAlbumName,
    getConsentClauseIds,
    hasConsent,
    isExpired,
    setConsent,
} from "../../utils/selectionToken";
import { fetchPreviousPicks } from "../../utils/selectionApi";
import {
    getFolderDisplayName,
    normalizeAlbumPath,
    toDeliveryPublicUrl,
    toThumbKey,
} from "../../utils/deliveryPath";
import {
    getPicks,
    getSelectorName,
    savePicks,
    setSelectorName as persistSelectorName,
} from "../../utils/selectionStore";
import { useTranslation } from "../../i18n";
import "./selection.css";

// 把服务端回填的 { path, name } 还原成完整的选片条目（补齐 id / 缩略图 / 下载地址）。
function reconstructPick(albumPath, { path, name, note }) {
    const root = normalizeAlbumPath(albumPath);
    const rel = normalizeAlbumPath(path);
    if (!rel) {
        return null;
    }
    const fullKey = root ? `${root}/${rel}` : rel;
    return {
        id: fullKey,
        name: name || rel.split("/").pop() || rel,
        path: rel,
        thumbnailUrl: toDeliveryPublicUrl(toThumbKey(fullKey)),
        downloadUrl: toDeliveryPublicUrl(fullKey),
        ...(note ? { note } : {}),
    };
}

function Selection() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { status, payload, reload } = useSelectionPayload(token);
    const [consented, setConsented] = useState(false);
    const [expired, setExpired] = useState(false);
    const [selectorName, setSelectorName] = useState("");
    const [picks, setPicks] = useState([]);
    // 每个链接只向服务端回填一次，避免重复合并。
    const syncedRef = useRef(false);

    useEffect(() => {
        syncedRef.current = false;
    }, [token]);

    const warnSaveFailed = useCallback(() => {
        message.warning({
            content: t("selection.gallery.saveFailed"),
            key: "selection-save-failed",
            duration: 5,
        });
    }, [t]);

    useEffect(() => {
        if (!payload) {
            setConsented(false);
            setExpired(false);
            return;
        }
        setExpired(isExpired(payload.e));
        setConsented(getConsentClauseIds(payload).length === 0 || hasConsent(token));
        setSelectorName(getSelectorName(token));
        setPicks(getPicks(token));
    }, [payload, token]);

    // 同名自动加载：当客户已同意并填写姓名后，拉取该姓名上次提交过的照片，
    // 与本地选片合并（按 id 去重）。换设备 / 清缓存后也能恢复之前的选片。
    useEffect(() => {
        if (!payload || !consented || !selectorName || syncedRef.current) {
            return undefined;
        }
        syncedRef.current = true;
        let cancelled = false;
        (async () => {
            const previous = await fetchPreviousPicks({ token, selectorName });
            if (cancelled || previous.length === 0) {
                return;
            }
            setPicks((current) => {
                const map = new Map(current.map((p) => [p.id, p]));
                let added = 0;
                previous.forEach((sp) => {
                    const entry = reconstructPick(payload.i, sp);
                    if (entry && !map.has(entry.id)) {
                        map.set(entry.id, entry);
                        added += 1;
                    }
                });
                if (added === 0) {
                    return current;
                }
                const merged = Array.from(map.values());
                if (!savePicks(token, merged)) {
                    warnSaveFailed();
                }
                message.success(t("selection.gallery.restored", { count: added }));
                return merged;
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [payload, consented, selectorName, token, t, warnSaveFailed]);

    useEffect(() => {
        const brand = "HEI CREATIVE";
        if (status === "loading") {
            document.title = `${t("selection.gallery.loadingAlbum")} · ${brand}`;
        } else if (status === "expired" || expired) {
            document.title = `${t("selection.meta.expiredLink")} · ${brand}`;
        } else if (!payload) {
            document.title = `${t("selection.meta.invalidLink")} · ${brand}`;
        } else if (consented && selectorName) {
            document.title = `${t("selection.meta.gallery")} · ${brand}`;
        } else {
            document.title = `${t("selection.meta.consent")} · ${brand}`;
        }
    }, [status, payload, expired, consented, selectorName, t]);

    const pickedIds = useMemo(() => new Set(picks.map((p) => p.id)), [picks]);

    const isPicked = useCallback((id) => pickedIds.has(id), [pickedIds]);

    const noteById = useMemo(() => {
        const map = new Map();
        picks.forEach((p) => {
            if (p.note) {
                map.set(p.id, p.note);
            }
        });
        return map;
    }, [picks]);

    const getNote = useCallback((id) => noteById.get(id) || "", [noteById]);

    // 填写修图指引时，如该照片尚未选中则自动加入选片（写修图要求意味着想保留这张）。
    const updatePickNote = useCallback(
        (entry, note) => {
            setPicks((prev) => {
                const idx = prev.findIndex((p) => p.id === entry.id);
                let next;
                if (idx === -1) {
                    next = [...prev, { ...entry, note }];
                } else {
                    next = prev.map((p) => (p.id === entry.id ? { ...p, note } : p));
                }
                if (!savePicks(token, next)) {
                    warnSaveFailed();
                }
                return next;
            });
        },
        [token, warnSaveFailed]
    );

    const togglePick = useCallback(
        (entry) => {
            setPicks((prev) => {
                const exists = prev.some((p) => p.id === entry.id);
                const next = exists
                    ? prev.filter((p) => p.id !== entry.id)
                    : [...prev, entry];
                if (!savePicks(token, next)) {
                    warnSaveFailed();
                }
                return next;
            });
        },
        [token, warnSaveFailed]
    );

    const handleAgree = useCallback(() => {
        setConsent(token);
        setConsented(true);
    }, [token]);

    const handleLeave = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleExpired = useCallback(() => {
        setExpired(true);
        setConsented(false);
    }, []);

    const handleNameSubmit = useCallback(
        (name) => {
            if (!persistSelectorName(token, name)) {
                warnSaveFailed();
            }
            setSelectorName(name);
        },
        [token, warnSaveFailed]
    );

    const handleViewCart = useCallback(() => {
        navigate(`/s/${token}/cart`);
    }, [navigate, token]);

    const albumDisplayName = payload
        ? getAlbumName(payload, getFolderDisplayName(payload.i)) ||
          t("selection.gallery.defaultAlbumName")
        : "";

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <Spin size="large" />
                        <p>{t("selection.gallery.loadingAlbum")}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (status === "expired") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (status === "error") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <p className="delivery-album-error__text">
                            {t("selection.gallery.albumLoadFailed")}
                        </p>
                        <div className="delivery-album-error__actions">
                            <Button type="primary" onClick={() => navigate("/contact")}>
                                {t("selection.gallery.contactPhotographer")}
                            </Button>
                            <Button onClick={reload}>{t("selection.gallery.retry")}</Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!payload) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ErrorPanel />
                </div>
                <Footer />
            </>
        );
    }

    if (expired || isExpired(payload.e)) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (!consented) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ConsentPanel
                        key={token}
                        expiryUtcSeconds={payload.e}
                        clauseIds={getConsentClauseIds(payload)}
                        clauses={SELECTION_CONSENT_CLAUSES}
                        consentNs="selection.consent"
                        onAgree={handleAgree}
                        onLeave={handleLeave}
                        onExpired={handleExpired}
                    />
                </div>
                <Footer />
            </>
        );
    }

    if (!selectorName) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <SelectionNameGate
                        albumName={albumDisplayName}
                        defaultName={getSelectorName(token)}
                        onSubmit={handleNameSubmit}
                        onBack={handleLeave}
                    />
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <div className="delivery-page delivery-page--gallery">
                <div className="delivery-gallery-bar">
                    <ExpiryCountdown
                        key={token}
                        expiryUtcSeconds={payload.e}
                        onExpired={handleExpired}
                        compact
                    />
                </div>
                <SelectionFolderBrowser
                    key={token}
                    albumPath={payload.i}
                    albumName={albumDisplayName}
                    downloadAll={payload.downloadAll || null}
                    linkId={token}
                    sourceType="selection"
                    isPicked={isPicked}
                    onTogglePick={togglePick}
                    getNote={getNote}
                    onUpdateNote={updatePickNote}
                    pickedCount={picks.length}
                    onViewCart={handleViewCart}
                />
            </div>
        </>
    );
}

export default Selection;
